/**
 * Type labels, said once where it is true. The connectors of one type leaving a
 * card run together out of it (one port per source and type, routeEdges.js) and
 * split where they must; the type is written once on that shared run, just after
 * its last corner, and never again on each line. One label per source and type;
 * labels of one source that would overlap step down their own run.
 *
 * Pure: edges in (with their routed points), the same edges out with `label`
 * (the point, on every member's path) and `labelLead` on the one that draws it.
 */
const same = (a, b) => Math.abs(a.x - b.x) < 0.01 && Math.abs(a.y - b.y) < 0.01;
const dist = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);

/** The polyline every route shares from its start: whole points, then the part of the next leg they still have in common. */
export function sharedRun(routes) {
  const first = routes[0];
  let k = 0;
  while (first[k] && routes.every((r) => r[k] && same(r[k], first[k]))) k += 1;
  const out = first.slice(0, k);
  const base = first[k - 1];
  const nexts = routes.map((r) => r[k]);
  if (nexts.every(Boolean)) {
    const dir = (p) => ({ dx: Math.sign(p.x - base.x), dy: Math.sign(p.y - base.y) });
    const d0 = dir(nexts[0]);
    if (nexts.every((p) => { const d = dir(p); return d.dx === d0.dx && d.dy === d0.dy; })) {
      const len = Math.min(...nexts.map((p) => dist(p, base)));
      out.push({ x: base.x + d0.dx * len, y: base.y + d0.dy * len });
    }
  }
  return out;
}

const length = (pts) => pts.slice(1).reduce((acc, q, i) => acc + dist(pts[i], q), 0);

/**
 * Where on a shared run a label goes: after the point where the last connector of
 * another type still ran alongside (`after`, an arc length along the run), so the
 * word names the lines that have branched out, not the bundle before the branch.
 * On the leg after the first corner first, as soon after the corner and the branch
 * as the label fits, stepping along when another label of the same source is
 * there; then the legs after; the first leg last. Never off the run.
 */
function place(run, clashes, config, after = 0) {
  const n = run.length - 1;
  const order = n >= 2 ? [...Array.from({ length: n - 1 }, (_, i) => i + 1), 0] : [0];
  let acc = 0;
  const starts = run.slice(0, -1).map((a, i) => { const s0 = acc; acc += dist(a, run[i + 1]); return s0; });
  const legs = order.map((i) => {
    const a = run[i];
    const b = run[i + 1];
    const horizontal = Math.abs(a.y - b.y) < 0.01;
    const clear = (horizontal ? config.labelHalfWidth : config.labelStep / 2) + config.labelGap;
    return { a, b, len: dist(a, b), min: i === 0 ? config.labelHalfWidth + config.labelGap : config.cornerRadius + config.labelGap + config.labelStep / 2, afterMin: Math.max(0, after + clear - starts[i]) };
  });
  const at = (leg, d) => ({ x: leg.a.x + Math.sign(leg.b.x - leg.a.x) * d, y: leg.a.y + Math.sign(leg.b.y - leg.a.y) * d });
  for (const leg of legs) {
    for (let d = Math.max(leg.min, leg.afterMin); d <= leg.len - config.labelGap; d += config.labelStep) {
      const p = at(leg, d);
      if (!clashes(p)) return p;
    }
  }
  // No room after the branch: the first spot that fits, branch or not.
  for (const leg of legs) {
    for (let d = leg.min; d <= leg.len - config.labelGap; d += config.labelStep) {
      const p = at(leg, d);
      if (!clashes(p)) return p;
    }
  }
  return at(legs[0], Math.min(legs[0].min, legs[0].len / 2));
}

export function labelBySource(edges, config) {
  const groups = new Map();
  edges.forEach((e) => {
    if (e.behind || !e.causal || e.kind === 'structural' || !e.type || !e.points?.length) return;
    const key = `${e.from}|${e.type}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  });
  const bySource = new Map();
  groups.forEach((members, key) => {
    const from = key.split('|')[0];
    if (!bySource.has(from)) bySource.set(from, []);
    bySource.get(from).push(members);
  });
  const step = config.labelStep;
  bySource.forEach((list) => {
    const placed = [];
    const clashes = (p) => placed.some((q) => Math.abs(q.x - p.x) < 2 * config.labelHalfWidth && Math.abs(q.y - p.y) < step);
    list.sort((a, b) => a[0].points[1].x - b[0].points[1].x || a[0].type.localeCompare(b[0].type));
    const all = list.flat();
    list.forEach((members) => {
      const run = sharedRun(members.map((e) => e.points));
      // How far along the run a connector of another type from this card still runs with it.
      const others = all.filter((e) => !members.includes(e));
      const after = others.length ? Math.max(...others.map((e) => length(sharedRun([run, e.points])))) : 0;
      const point = place(run, clashes, config, after);
      placed.push(point);
      members.forEach((e, i) => { e.label = point; e.labelLead = i === 0; e.labelGroup = members.length; });
    });
  });
  return edges;
}
